import { useEffect, useRef, useImperativeHandle, forwardRef } from "react";

/**
 * Mobile-robust signature canvas.
 *
 * Why this exists (vs. React's native onPointerDown/onTouchStart):
 *   React attaches touch listeners as `{ passive: true }`, so e.preventDefault()
 *   inside the handler is silently ignored and the page scrolls under the
 *   user's finger instead of drawing. This component attaches the touch +
 *   pointer listeners via addEventListener with `{ passive: false }` so
 *   preventDefault actually works on iOS Safari, Android Chrome, in-app
 *   browsers and stylus devices.
 *
 * Forwards ref → exposes `toDataURL()` and `clear()`.
 */
const SignatureCanvas = forwardRef(function SignatureCanvas(
  {
    width = 520,
    height = 140,
    background,
    strokeStyle,
    lineWidth = 2.5,
    onDrawn,                 // fires once when the user has actually drawn something
    "data-testid": dataTestId = "signature-canvas",
    className = "",
    style = {},
  },
  ref,
) {
  // Phase 2.46.122 — Theme-aware defaults.  Dark mode keeps the original
  // dark-navy canvas + white stroke (and post-process inversion for PDF
  // export).  Light mode renders a white canvas + dark-navy stroke and
  // exports the canvas as-is (no inversion needed).
  const _isLight = typeof document !== "undefined"
    && document.documentElement.getAttribute("data-theme") === "light";
  const _bg = background || (_isLight ? "#FFFFFF" : "#0D1F3C");
  const _stroke = strokeStyle || (_isLight ? "#0A1628" : "#FFFFFF");

  const canvasRef = useRef(null);
  const drawing  = useRef(false);
  const drewOnce = useRef(false);
  const _bgRef = useRef(_bg);
  const _strokeRef = useRef(_stroke);
  _bgRef.current = _bg;
  _strokeRef.current = _stroke;

  const _init = (cvs) => {
    if (!cvs) return;
    const ctx = cvs.getContext("2d");
    ctx.fillStyle = _bgRef.current;
    ctx.fillRect(0, 0, cvs.width, cvs.height);
    ctx.strokeStyle = _strokeRef.current;
    ctx.lineWidth   = lineWidth;
    ctx.lineCap     = "round";
    ctx.lineJoin    = "round";
    drewOnce.current = false;
  };

  useImperativeHandle(ref, () => ({
    clear: () => {
      _init(canvasRef.current);
    },
    /**
     * Returns a PNG data URL where the on-canvas (UI-friendly) dark bg + white
     * strokes have been inverted to white bg + dark navy strokes — so the
     * exported signature looks correct on the WHITE page of the Zydus PDF.
     *
     * Pixel processing: any pixel close to the background fill (#0D1F3C) is
     * turned into pure white; any other pixel (the stroke) is normalised to
     * dark navy (#0A1628). Anti-aliased edge pixels blend smoothly because
     * we keep their alpha/intensity.
     */
    toDataURL: (type = "image/png") => {
      const cvs = canvasRef.current;
      if (!cvs) return null;
      // Light-mode canvas is already white-bg + dark-stroke — perfect for
      // PDF export, no inversion needed.
      if (_isLight) {
        try { return cvs.toDataURL(type); } catch (_) { return null; }
      }
      try {
        const off = document.createElement("canvas");
        off.width = cvs.width;
        off.height = cvs.height;
        const octx = off.getContext("2d");
        // Start with a solid white page-friendly background
        octx.fillStyle = "#FFFFFF";
        octx.fillRect(0, 0, off.width, off.height);
        // Read the source pixels, recolour the strokes to dark and skip bg
        const src = cvs.getContext("2d").getImageData(0, 0, cvs.width, cvs.height);
        const dst = octx.getImageData(0, 0, off.width, off.height);
        const data = src.data, out = dst.data;
        // Source bg #0D1F3C ~ (13,31,60). Treat anything within Euclidean
        // distance ≤ 60 from that colour as background.
        const BG_R = 13, BG_G = 31, BG_B = 60;
        const STROKE_R = 10, STROKE_G = 22, STROKE_B = 40; // dark navy ink
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i], g = data[i+1], b = data[i+2];
          const dr = r - BG_R, dg = g - BG_G, db = b - BG_B;
          const dist = Math.sqrt(dr*dr + dg*dg + db*db);
          if (dist > 60) {
            // Stroke pixel — paint dark navy with intensity proportional to
            // how far it is from the bg (gives smooth anti-aliased edges)
            const t = Math.min(1, (dist - 60) / 160);
            out[i]   = Math.round(STROKE_R * t + 255 * (1 - t));
            out[i+1] = Math.round(STROKE_G * t + 255 * (1 - t));
            out[i+2] = Math.round(STROKE_B * t + 255 * (1 - t));
            out[i+3] = 255;
          } else {
            // Background — fully white
            out[i] = 255; out[i+1] = 255; out[i+2] = 255; out[i+3] = 255;
          }
        }
        octx.putImageData(dst, 0, 0);
        return off.toDataURL(type);
      } catch (e) {
        // Last-ditch fallback if the offscreen pipeline ever fails
        return cvs.toDataURL(type);
      }
    },
    hasDrawn: () => drewOnce.current,
  }), []);

  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    _init(cvs);

    const pointAt = (clientX, clientY) => {
      const r = cvs.getBoundingClientRect();
      const sx = cvs.width / r.width;
      const sy = cvs.height / r.height;
      return { x: (clientX - r.left) * sx, y: (clientY - r.top) * sy };
    };

    const start = (clientX, clientY) => {
      drawing.current = true;
      const { x, y } = pointAt(clientX, clientY);
      const ctx = cvs.getContext("2d");
      ctx.beginPath(); ctx.moveTo(x, y);
    };
    const move = (clientX, clientY) => {
      if (!drawing.current) return;
      const { x, y } = pointAt(clientX, clientY);
      const ctx = cvs.getContext("2d");
      ctx.lineTo(x, y); ctx.stroke();
      if (!drewOnce.current) {
        drewOnce.current = true;
        onDrawn && onDrawn();
      }
    };
    const stop = () => { drawing.current = false; };

    // --- Touch (non-passive so preventDefault works on iOS Safari) ---
    const onTouchStart = (e) => {
      if (!e.touches?.length) return;
      e.preventDefault();
      const t = e.touches[0];
      start(t.clientX, t.clientY);
    };
    const onTouchMove = (e) => {
      if (!e.touches?.length) return;
      e.preventDefault();
      const t = e.touches[0];
      move(t.clientX, t.clientY);
    };
    const onTouchEnd = (e) => {
      e.preventDefault();
      stop();
    };

    // --- Mouse fallback ---
    const onMouseDown = (e) => { e.preventDefault(); start(e.clientX, e.clientY); };
    const onMouseMove = (e) => { if (drawing.current) { e.preventDefault(); move(e.clientX, e.clientY); } };
    const onMouseUp   = () => stop();
    const onMouseLeave= () => stop();

    cvs.addEventListener("touchstart", onTouchStart, { passive: false });
    cvs.addEventListener("touchmove",  onTouchMove,  { passive: false });
    cvs.addEventListener("touchend",   onTouchEnd,   { passive: false });
    cvs.addEventListener("touchcancel",onTouchEnd,   { passive: false });
    cvs.addEventListener("mousedown",  onMouseDown);
    cvs.addEventListener("mousemove",  onMouseMove);
    cvs.addEventListener("mouseup",    onMouseUp);
    cvs.addEventListener("mouseleave", onMouseLeave);

    return () => {
      cvs.removeEventListener("touchstart", onTouchStart);
      cvs.removeEventListener("touchmove",  onTouchMove);
      cvs.removeEventListener("touchend",   onTouchEnd);
      cvs.removeEventListener("touchcancel",onTouchEnd);
      cvs.removeEventListener("mousedown",  onMouseDown);
      cvs.removeEventListener("mousemove",  onMouseMove);
      cvs.removeEventListener("mouseup",    onMouseUp);
      cvs.removeEventListener("mouseleave", onMouseLeave);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      data-testid={dataTestId}
      className={`w-full rounded-lg cursor-crosshair ${className}`}
      style={{
        border: _isLight
          ? "1px solid rgba(15, 23, 42, 0.18)"
          : "1px solid rgba(255,255,255,0.08)",
        touchAction: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        ...style,
      }}
    />
  );
});

export default SignatureCanvas;
